import { inject, Injectable } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { keys } from '@sama/micro-dash';
import { forEach, pick, values } from 'lodash';
import { deleteShapes } from '../../../cross-team-shared/annotation/shared/state/layers/shape-layer-state';
import { ShapeState } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { MessagingService } from '../../../cross-team-shared/messaging/messaging.service';
import { getOrThrowStore } from '../../../cross-team-shared/misc-utils/assert-store';
import { isOrthographicVideoOutput } from '../../../cross-team-shared/misc-utils/output-utils';
import { strictValues } from '../../../cross-team-shared/misc-utils/strict-record';
import { addUngroupedShape } from '../../../cross-team-shared/state/shape.state';
import { pickExisting } from '../../../shared/misc-utilities/misc-utils';
import { ShapeGroupOrderState } from '../../../team-apis/content/content-connector';
import { AnnotationOutput } from '../../../team-apis/otterdam/outputs/annotation-output';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { getOutputAnnotationInfoStore } from '../../h3ml/state/task-workspace-store-utils';
import { FusionManagerFactory } from '../../outputs/annotation/annotation-shared/fusion/fusion-manager-factory.service';
import { flipYCoordinate } from '../../outputs/annotation/annotation-shared/fusion/projection/orthographic-orchestrator';
import { AnnotationColorService } from './annotation-color.service';
import { AnnotationFilteringService } from './annotation-filtering.service';

@Injectable()
export class ShapeService {
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private annotationColorService = inject(AnnotationColorService);
  private annotationFilteringService = inject(AnnotationFilteringService);
  private fusionManagerFactory = inject(FusionManagerFactory);
  private messagingService = inject(MessagingService);

  createShape(shape: ShapeState, annotationOutputName: string) {
    shape.outputName = annotationOutputName;
    const annotationInfoStore = getOutputAnnotationInfoStore(
      this.taskWorkspaceStore,
      annotationOutputName,
    );
    this.taskWorkspaceStore.batch(() => {
      addUngroupedShape(shape, annotationInfoStore);
      this.taskWorkspaceStore('shapeTransientInfo')(shape.index).set({
        isFilteredOut:
          this.annotationFilteringService.calculateIsShapeFilteredOut(shape),
        color: this.annotationColorService.getShapeColor(shape),
        hasMissingRequiredTags: false,
      });
    });
    return shape;
  }

  updateKeyLocationsAndSource(
    shapeGroupOrderState: ShapeGroupOrderState,
    annotationOutput: AnnotationOutput,
  ) {
    let shapes = strictValues(shapeGroupOrderState.shapes);
    if (isOrthographicVideoOutput(annotationOutput)) {
      shapes = shapes.map((shape) =>
        this.groundShape(shape, annotationOutput.id),
      );
    }

    const shapesStore = this.taskWorkspaceStore.getShapesStore(
      annotationOutput.name,
    );
    shapesStore.getRootStore().batch(() => {
      shapes.forEach((shape) => {
        getOrThrowStore(shapesStore, shape.index).assign(
          pick(shape, 'key_locations', 'source'),
        );
      });
    });
  }

  deleteShapes(shapeIndices: number[], showMessage = true) {
    this.taskWorkspaceStore.batch(() => {
      this.removeFromSelection(shapeIndices);
      this.removeTransientInfo(shapeIndices);
      keys(this.taskWorkspaceStore.state().outputAnnotationInfo).forEach(
        (annotationOutputName) =>
          deleteShapes(
            shapeIndices,
            getOutputAnnotationInfoStore(
              this.taskWorkspaceStore,
              annotationOutputName,
            ),
          ),
      );
    });
    if (showMessage) {
      this.messagingService.show(`${shapeIndices.length} item(s) deleted`);
    }
  }

  /** an optimized way of fetching multiple shapes without needing to iterate
   * through all shapes */
  getShapes(shapeIndices: number[]) {
    const { outputAnnotationInfo } = this.taskWorkspaceStore.state();
    const shapes: ShapeState[] = [];

    forEach(outputAnnotationInfo, (shapeInfoForOutput) => {
      shapes.push(
        // @ts-ignore -- fix me
        ...values(pickExisting(shapeInfoForOutput.shapes, ...shapeIndices)),
      );
    });

    return shapes;
  }

  getShapeStore(shapeIndex: number) {
    const shape = this.getShapes([shapeIndex])[0];
    assert(
      shape.outputName,
      'shape should have outputName set when using Objects',
    );

    return this.taskWorkspaceStore.getShapeStore(shape.outputName, shapeIndex);
  }

  getShapeStores(shapeIds: number[]) {
    return shapeIds.map((shapeId) => this.getShapeStore(shapeId));
  }

  shapeExists(shapeIndex: number) {
    return isDefined(this.getShapes([shapeIndex])[0]);
  }

  groundShape(shape: ShapeState, outputId: number) {
    const fusionManager = this.fusionManagerFactory.getManager(outputId);
    const pointCloudContentConnector = fusionManager
      .getPrimaryMinion()
      .getPointCloudContentConnector();
    return {
      ...shape,
      key_locations: shape.key_locations.map((location) => ({
        ...location,
        points: location.points.map((point) => {
          const convertedPoint = flipYCoordinate(point);
          if (
            location.frame_number === this.getFrameNumber(outputId) &&
            (point[2] === undefined || isNaN(point[2]))
          ) {
            return pointCloudContentConnector.groundPoint(convertedPoint);
          }
          return convertedPoint;
        }),
      })),
    };
  }

  private getFrameNumber(outputId: number) {
    return this.fusionManagerFactory.getManager(outputId).fusionStore.state()
      .frameNumber;
  }

  private removeFromSelection(shapeIndices: number[]) {
    this.taskWorkspaceStore('shapeSelection').mutateUsing((selection) =>
      shapeIndices.forEach((index) => delete selection[index]),
    );
  }

  private removeTransientInfo(shapeIndices: number[]) {
    shapeIndices.forEach((shapeIndex) =>
      this.taskWorkspaceStore('shapeTransientInfo')(shapeIndex).delete(),
    );
  }
}
